/**
 * Reusable US-state pickers used wherever the platform asks for a
 * state code — the rep's home state and the admin-side territory
 * multi-select.
 *
 * Built on MUI Autocomplete so users can type the state code OR the
 * name ("FL" or "Florida" both work) and there's no chance of bad
 * input like "FA" sneaking through to the backend.
 */

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material';

import { US_STATES, US_STATE_NAME_BY_CODE, type UsState } from '../../util/usStates';
import { isControlState } from '../../lib/controlStates';

const CHECK_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECK_ICON_SELECTED = <CheckBoxIcon fontSize="small" />;
const CONTROL_TONE = '#7d3c4a';

// Filter by "code · name" so the user can type either ("FL" OR
// "florida") and still match — even when getOptionLabel returns just
// the code so the collapsed input stays compact.
const STATE_FILTER = createFilterOptions<UsState>({
  stringify: (s) => `${s.code} ${s.name}`,
});
const CONTROL_TOOLTIP =
  'NABCA control state — the state government runs wholesale (and sometimes retail) spirits distribution. Selling here goes through the state ABC board, not private distributors.';

/** Small wine-tinted "Control" pill rendered alongside each NABCA
 *  control state in the dropdown.  Tooltip explains why it matters
 *  for sales-rep assignment. */
function ControlBadge() {
  return (
    <Tooltip title={CONTROL_TOOLTIP} arrow placement="top">
      <Box
        component="span"
        sx={{
          ml: 1,
          px: 0.7,
          py: 0.15,
          borderRadius: 0.6,
          bgcolor: 'rgba(125, 60, 74, 0.12)',
          color: CONTROL_TONE,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          border: '1px solid rgba(125, 60, 74, 0.25)',
        }}
      >
        Control
      </Box>
    </Tooltip>
  );
}

const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface HomeStatePickerProps {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  size?: 'small' | 'medium';
  width?: number | string;
}

/** Single-state picker — used for the rep's home_state field.
 *  The COLLAPSED input shows just the 2-letter code (compact + always
 *  fits in narrow form rows); the dropdown OPTIONS show
 *  "FL · Florida" with the optional control-state badge so the rep
 *  can confirm the full name when choosing.  This pattern beats
 *  showing the full label in the input because the input gets
 *  truncated in narrow grid cells like the City / State / ZIP row. */
export function HomeStatePicker({
  value,
  onChange,
  label = 'State',
  size = 'small',
  width = 200,
}: HomeStatePickerProps) {
  const selected = US_STATES.find((s) => s.code === value) ?? null;
  return (
    <Autocomplete<UsState>
      value={selected}
      onChange={(_, next) => onChange(next?.code ?? '')}
      options={US_STATES}
      getOptionLabel={(s) => s.code}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      filterOptions={STATE_FILTER}
      size={size}
      sx={{ width }}
      // The dropdown inherits the input's narrow width by default.
      // Force a comfortable minimum on the popup so options like
      // "CT · Connecticut" stay on a single line.
      slotProps={{
        paper: { sx: { minWidth: 260 } },
        listbox: { sx: { '& .MuiAutocomplete-option': { whiteSpace: 'nowrap' } } },
      }}
      renderOption={(props, option) => {
        const { key, ...liProps } = props;
        return (
          <li key={key} {...liProps}>
            {option.code} · {option.name}
            {isControlState(option.code) && <ControlBadge />}
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Select…" />}
    />
  );
}

interface TerritoryPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  label?: string;
}

/** Multi-state picker — used for territory_states.  Renders selected
 *  states as gold chips so they read at a glance.
 */
export function TerritoryStatePicker({
  value,
  onChange,
  label = 'Territory states',
}: TerritoryPickerProps) {
  const selected = value
    .map((code) => US_STATES.find((s) => s.code === code))
    .filter((s): s is UsState => !!s);

  return (
    <Autocomplete<UsState, true>
      multiple
      // Keep the dropdown open while admin ticks several states — the
      // common case is picking 2–4 in a row, not one at a time.
      disableCloseOnSelect
      value={selected}
      onChange={(_, next) => onChange(next.map((s) => s.code).sort((a, b) => a.localeCompare(b)))}
      options={US_STATES}
      getOptionLabel={(s) => `${s.code} · ${s.name}`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      size="small"
      renderOption={(props, option, { selected: isSelected }) => {
        // MUI Autocomplete returns the React key inside the spread
        // props object — destructure so React doesn't warn about a
        // spread+key clash.
        const { key, ...liProps } = props;
        return (
          <li key={key} {...liProps}>
            <Checkbox
              icon={CHECK_ICON}
              checkedIcon={CHECK_ICON_SELECTED}
              checked={isSelected}
              sx={{
                py: 0.5,
                color: 'rgba(15, 10, 7, 0.35)',
                '&.Mui-checked': { color: GOLD },
                mr: 0.5,
              }}
            />
            {option.code} · {option.name}
            {isControlState(option.code) && <ControlBadge />}
          </li>
        );
      }}
      renderTags={(items, getTagProps) =>
        items.map((s, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const isControl = isControlState(s.code);
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={s.code}
              title={`${US_STATE_NAME_BY_CODE[s.code]}${isControl ? ' · Control state' : ''}`}
              sx={{
                fontFamily: FONT_INTER,
                fontWeight: 600,
                ...(isControl
                  ? {
                      bgcolor: 'rgba(125, 60, 74, 0.10)',
                      color: CONTROL_TONE,
                      border: '1px solid rgba(125, 60, 74, 0.32)',
                    }
                  : {
                      bgcolor: 'rgba(187, 140, 63, 0.08)',
                      color: GOLD,
                      border: '1px solid rgba(187, 140, 63, 0.32)',
                    }),
              }}
            />
          );
        })
      }
      slotProps={{
        paper: { sx: { minWidth: 260 } },
        listbox: { sx: { '& .MuiAutocomplete-option': { whiteSpace: 'nowrap' } } },
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Add a state…" />}
    />
  );
}
